import { TrendingUp, TrendingDown, Clock, DollarSign, ArrowUpRight, ArrowDownRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface DashboardMetricsSectionProps {
  realHourlyRate: number
  targetHourlyRate: number
  totalMonthHours: number
  hoursGrowthPercent: number
  previousMonthHours: number
  revenueGrowthPercent: number
}

const formatCurrency = (value: number) => 
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const formatHours = (hours: number) => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${m}min`
}

export function DashboardMetricsSection({
  realHourlyRate,
  targetHourlyRate,
  totalMonthHours,
  hoursGrowthPercent,
  previousMonthHours,
  revenueGrowthPercent
}: DashboardMetricsSectionProps) {
  const isRateOnTarget = realHourlyRate >= targetHourlyRate * 0.9
  const isRateAboveTarget = realHourlyRate >= targetHourlyRate

  const metrics = [
    {
      id: 'hourly-rate',
      title: 'Valor/Hora Real',
      value: formatCurrency(realHourlyRate),
      subtitle: `Meta: ${formatCurrency(targetHourlyRate)}/h`,
      icon: DollarSign,
      trend: isRateAboveTarget 
        ? { value: ((realHourlyRate / targetHourlyRate - 1) * 100), positive: true }
        : isRateOnTarget 
          ? null 
          : { value: ((1 - realHourlyRate / targetHourlyRate) * 100), positive: false },
      iconBg: isRateAboveTarget 
        ? 'bg-success/10' 
        : isRateOnTarget 
          ? 'bg-primary/10' 
          : 'bg-warning/10',
      iconColor: isRateAboveTarget 
        ? 'text-success' 
        : isRateOnTarget 
          ? 'text-primary' 
          : 'text-warning',
      valueColor: isRateAboveTarget 
        ? 'text-success' 
        : isRateOnTarget 
          ? 'text-foreground' 
          : 'text-warning'
    },
    {
      id: 'total-hours',
      title: 'Total de Horas',
      value: formatHours(totalMonthHours),
      subtitle: `Mês anterior: ${formatHours(previousMonthHours)}`,
      icon: Clock,
      trend: hoursGrowthPercent !== 0 
        ? { value: Math.abs(hoursGrowthPercent), positive: hoursGrowthPercent > 0 }
        : null,
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary',
      valueColor: 'text-foreground'
    },
    {
      id: 'revenue-growth',
      title: 'Crescimento Receita',
      value: `${revenueGrowthPercent >= 0 ? '+' : ''}${revenueGrowthPercent.toFixed(1)}%`,
      subtitle: 'vs mês anterior',
      icon: revenueGrowthPercent >= 0 ? TrendingUp : TrendingDown,
      trend: null,
      iconBg: revenueGrowthPercent >= 0 ? 'bg-success/10' : 'bg-destructive/10',
      iconColor: revenueGrowthPercent >= 0 ? 'text-success' : 'text-destructive',
      valueColor: revenueGrowthPercent >= 0 ? 'text-success' : 'text-destructive'
    },
    {
      id: 'hours-growth',
      title: 'Crescimento Horas',
      value: `${hoursGrowthPercent >= 0 ? '+' : ''}${hoursGrowthPercent.toFixed(1)}%`,
      subtitle: 'vs mês anterior',
      icon: hoursGrowthPercent >= 0 ? TrendingUp : TrendingDown,
      trend: null,
      iconBg: hoursGrowthPercent >= 0 ? 'bg-success/10' : 'bg-destructive/10',
      iconColor: hoursGrowthPercent >= 0 ? 'text-success' : 'text-destructive',
      valueColor: hoursGrowthPercent >= 0 ? 'text-success' : 'text-destructive'
    }
  ]

  return (
    <section>
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <TrendingUp className="h-5 w-5 text-primary" />
        Métricas de Performance
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric) => (
          <Card key={metric.id} className="shadow-card hover:shadow-card-lg transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">{metric.title}</p>
                  <p className={cn("text-2xl font-bold tracking-tight", metric.valueColor)}>
                    {metric.value}
                  </p>
                  <p className="text-xs text-muted-foreground">{metric.subtitle}</p>
                  {metric.trend && (
                    <p className={cn(
                      "text-xs font-medium flex items-center gap-1 mt-1",
                      metric.trend.positive ? "text-success" : "text-destructive"
                    )}>
                      {metric.trend.positive ? (
                        <ArrowUpRight className="h-3 w-3" />
                      ) : (
                        <ArrowDownRight className="h-3 w-3" />
                      )}
                      {metric.trend.positive ? '+' : '-'}{metric.trend.value.toFixed(1)}% da meta
                    </p>
                  )}
                </div>
                <div className={cn("rounded-xl p-3", metric.iconBg)}>
                  <metric.icon className={cn("h-5 w-5", metric.iconColor)} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  )
}
